/* eslint-disable eqeqeq */
import { ACCESSORY_FORM_CONFIGS } from '../accessoryFormConfigs';
import { useAccessoryForm } from '../useAccessoryForm';
import { AccessorySelectHeading } from '../accessoryFieldKit';
import type { AccessoryFormProps } from '../AccessoryFormHost';

/**
 * Phase 6e2b-2 — UVC form (accessories/uvc/{view/form.js 190, tmpl/form.tmpl
 * 482}, read in full). §50.1/§51.2 + parity 327:
 *  - the WHOLE form body (lines 20-478 of the tmpl) is COMMENTED OUT in
 *    legacy — the live render is the form-heading only: a hidden "UVC System"
 *    btn-sync + the selection-mode radio/label pair. Replicated exactly;
 *  - the data_uvc 13-sub-catalog field is flattened to 13 chassis engines
 *    (per-catalog CMSPages/filters/counts — all noFetchCount) so the Q15 shim
 *    exposes them to the uvc selector (6e2c); the [NEG] lamp_params collision
 *    (current_sensor) is replicated in the config;
 *  - model.accessory.uvc NEVER auto-saves (§49.2 guard in autoSaveModel —
 *    parity 300); persistence is selector-owned via the §20 #62
 *    ahu/uvc/{power_supply,monitor,lamp}_actual_qty endpoints (6e2c);
 *  - uvc is in the no-auto-position list; form.js sets neither withPosition
 *    nor setupMounting (the §51.1 popup census row belongs to the
 *    selector-embedded MountingView, not this form).
 */
const config = ACCESSORY_FORM_CONFIGS.uvc;

const UvcForm = ({ row, section, tunnel, expanded }: AccessoryFormProps) => {
  const api = useAccessoryForm({
    config,
    row,
    section,
    tunnel,
    expanded,
    labeledSubtabs: [],
  });
  if (api.loading || !api.model) {
    return <div className="preloader dotted-preloader active" />;
  }
  const uvcsCount = api.catalogueFields.power_supply_id?.cmsCount;
  return (
    <div ref={(el) => { api.formRef.current = el; }}>
      <AccessorySelectHeading
        api={api}
        type="uvc"
        // ★ "Rating", not "Select". The four other accessory templates hardcode
        // the word `Select` (acc damper/hood/louver/opening form.tmpl), but the
        // UVC one is the odd one out: it renders `{{name}}` from the
        // `uvc_selection_modes` collection, filtered to `value != 'manual'`
        // (accessories/uvc/tmpl/form.tmpl:8-13). That collection is
        // `/api/v1.0/master/v1.0.0/ahu/select_modes/`, which has exactly two
        // rows — `1 manual "Manual"` and `2 rating "Rating"` — so the surviving
        // label always reads "Rating". Hardcoded here for the same reason the
        // other four are: it is a single fixed word, not a live choice.
        labelText=" Rating"
        // the selector's Project tab reads section.name/.tag and its
        // target-surface default reads section.type — none of which survive the
        // shim-derived {id, ...dims} payload.
        section={section as unknown as Record<string, unknown>}
        extraChrome={
          <button
            type="button"
            name="uvc"
            data-name="uvc"
            className={`btn hide btn-sync vis-uvc${
              uvcsCount == 1 || uvcsCount == 0 ? ' synced ' : ''
            } clickable`}
          >
            <span className="uvcs_search_result search-result">UVC System</span>
          </button>
        }
      />
    </div>
  );
};

export default UvcForm;
